import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { apiError } from '../lib/utils';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  Package,
  FileText,
  Building2,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Calendar,
  Receipt,
  Wallet,
  ReceiptText,
  FileMinus,
  Truck,
  UserCog,
  Activity,
  KeyRound,
  Loader2,
  HandCoins,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const EMPTY_PW = () => ({ current: '', next: '', confirm: '' });

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/employees', label: 'Employees', icon: Users },
  { path: '/services', label: 'Services & Packages', icon: Package },
  { path: '/clients', label: 'Clients', icon: Building2 },
  { path: '/proposals', label: 'Proposals', icon: FileText },
  { path: '/invoices', label: 'Invoices', icon: ReceiptText },
  { path: '/payments-received', label: 'Payments Received', icon: HandCoins },
  { path: '/credit-notes', label: 'Credit Notes', icon: FileMinus },
  { path: '/expenses', label: 'Expenses', icon: Receipt },
  { path: '/vendors', label: 'Vendors', icon: Truck },
  { path: '/accounts', label: 'Accounts', icon: Wallet },
  { path: '/financials', label: 'Financials', icon: BarChart3 },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/workforce', label: 'Workforce', icon: Activity, perm: 'workforce' },
  { path: '/users', label: 'Users', icon: UserCog, perm: 'users' },
  { path: '/settings', label: 'Settings', icon: Settings, perm: 'settings_write' },
];

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, can } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState(EMPTY_PW());
  const [savingPw, setSavingPw] = useState(false);

  const openPw = () => {
    setPw(EMPTY_PW());
    setSidebarOpen(false);
    setPwOpen(true);
  };

  const submitPw = async (e) => {
    e.preventDefault();
    if (pw.next.length < 8) return toast.error('New password must be at least 8 characters');
    if (pw.next !== pw.confirm) return toast.error('New passwords do not match');
    setSavingPw(true);
    try {
      await authAPI.changePassword(pw.current, pw.next);
      toast.success('Password changed');
      setPwOpen(false);
    } catch (err) {
      toast.error(apiError(err, 'Failed to change password'));
    } finally {
      setSavingPw(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const visibleNavItems = navItems.filter(item => !item.perm || can(item.perm));
  const currentPage = visibleNavItems.find(item => location.pathname.startsWith(item.path))?.label || 'Dashboard';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile menu button */}
      <button
        data-testid="mobile-menu-btn"
        aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-black text-white rounded-md"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
      </button>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — flex column: header / scrollable nav / user section.
          (Previously the user section was absolute-bottom and the 13-item nav
          slid underneath it on shorter screens.) */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-black text-white z-50 transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6 border-b border-white/10 shrink-0">
          <h1 className="text-xl font-bold tracking-tight font-['Manrope']">PriceIntel</h1>
          <p className="text-xs text-gray-400 mt-1">Cost Intelligence System</p>
        </div>

        <nav className="py-4 flex-1 min-h-0 overflow-y-auto">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '')}`}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-6 py-2.5 transition-all duration-200 ${
                  isActive
                    ? 'bg-white text-black font-medium'
                    : 'text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon size={18} strokeWidth={1.5} />
                <span className="text-sm">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="shrink-0 p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-sm font-medium">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-gray-400 truncate">{user?.role || 'viewer'}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/10"
            onClick={openPw}
          >
            <KeyRound size={16} className="mr-2" />
            Change password
          </Button>
          <Button
            data-testid="logout-btn"
            variant="ghost"
            size="sm"
            className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
          >
            <LogOut size={16} className="mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPw} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pw-current">Current password</Label>
              <Input id="pw-current" type="password" autoComplete="current-password" required
                value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-next">New password</Label>
              <Input id="pw-next" type="password" autoComplete="new-password" required minLength={8}
                value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} />
              <p className="text-xs text-gray-500">At least 8 characters</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw-confirm">Confirm new password</Label>
              <Input id="pw-confirm" type="password" autoComplete="new-password" required
                value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setPwOpen(false)} disabled={savingPw}>
                Cancel
              </Button>
              <Button type="submit" disabled={savingPw} className="bg-black hover:bg-black/90 text-white">
                {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Header — pl-16 on mobile clears the floating hamburger button */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200 pl-16 pr-6 py-4 lg:px-8">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="hidden sm:inline">PriceIntel</span>
            <ChevronRight size={14} className="hidden sm:inline" />
            <span className="font-medium text-black">{currentPage}</span>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
